import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { api } from "../../../../../api/api";
import RulesTable from "pages/Products/components/RulesTable/RulesTable.component";
import { useTypedSelector } from "store";
import { RuleTableResponse } from "types/table";

export type ImportTSVReplaceTablePreviewProps = {
  tableId: string;
};

export function ImportTSVReplaceTablePreview({
  tableId,
}: ImportTSVReplaceTablePreviewProps) {
  const productLineId = useTypedSelector(
    (state) => state.products.selectedProductLine?.id
  );
  const cachedTable = useTypedSelector(
    (state) =>
      state.products.tables.byId[tableId] as RuleTableResponse | undefined
  );

  const [table, setTable] = useState<RuleTableResponse | undefined>(
    cachedTable
  );
  const [loading, setLoading] = useState(!cachedTable);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (cachedTable) {
      setTable(cachedTable);
      setLoading(false);
      setError(null);
      return;
    }

    if (!productLineId || !tableId) {
      setLoading(false);
      setError("Could not load the matching table.");
      return;
    }

    setLoading(true);
    setError(null);

    void api
      .getRuleTable(productLineId, tableId)
      .then((response) => {
        if (cancelled) return;
        if (response.status >= 400 || !response.data) {
          setError("Could not load the matching table.");
          setTable(undefined);
          return;
        }
        setTable(response.data);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Could not load the matching table.");
        setTable(undefined);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cachedTable, productLineId, tableId]);

  return (
    <PreviewRoot>
      <Typography variant="subtitle2">
        Existing table that would be replaced
      </Typography>

      {loading && (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading matching table…</Typography>
        </Box>
      )}

      {error && !loading && <Alert severity="warning">{error}</Alert>}

      {table && !loading && (
        <TableScroll>
          <RulesTable id={table.id} ruleTableData={table} variant="preview" />
        </TableScroll>
      )}
    </PreviewRoot>
  );
}

const PreviewRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TableScroll = styled.div`
  max-height: min(55vh, 560px);
  overflow: auto;
  border: 1px solid ${(props) => props.theme.colors.tables.borders};
  border-radius: 4px;
  background: ${(props) => props.theme.colors.white};
`;
